

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useState } from 'react';


import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { Event } from '@/lib/types';
import { getNextRelationshipGraphPosition } from '@/lib/relationship-graph-layout';

interface QuickEventDialogProps {
  open: boolean;
  onClose: () => void;
  initialStructureSectionId?: string | null;
}

export function QuickEventDialog({
  open,
  onClose,
  initialStructureSectionId,
}: QuickEventDialogProps) {
  const {
    novel,
    firestore,
    getNewId,
    saveEntity
  } = useNovelRepository();
  const novelId = novel?.id;
  const { toast } = useToast();

  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async () => {
    if (!firestore || !novelId) return;

    if (!title.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Event title is required.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    const now = new Date().toISOString();
    try {
      const eventId = getNewId();
      const graphPosition = getNextRelationshipGraphPosition(novel);
      const eventData: Event = {
        id: eventId,
        name: title.trim(),
        type: '',
        date: date.trim() || '',
        shortDescription: '',
        locationIds: [],
        characterIds: [],
        organizationIds: [],
        structureSectionId: initialStructureSectionId || undefined,
        isNarrativelyComplete: false,
        graphPosition,
        audit: {
          createdAt: now,
          updatedAt: now
        }
      };

      await saveEntity('events', eventData);

      toast({
        title: 'Event Created',
        description: `${title} has been added to the timeline.`,
      });

      // Reset form
      setTitle('');
      setDate('');
      onClose();
    } catch (error) {
      console.error('Create event error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create event.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDate('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Quick Create Event</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Title */}
          <TextField
            label="Event Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            autoFocus
          />

          {/* Date */}
          <TextField
            label="Date / Date Range"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            placeholder="e.g., Spring 1204, Day 5"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          Create Event
        </Button>
      </DialogActions>
    </Dialog>
  );
}
